package com.example.monetix.db

import app.cash.sqldelight.async.coroutines.awaitAsList
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock

// ==========================================
// 🚀 REPOSITORY BLINDATO (Anti-Sparizione)
// ==========================================
class MonetixRepository(database: MonetixDatabase) {
    private val accountQueries = database.accountQueries
    private val transactionQueries = database.transactionQueries
    private val categoryQueries = database.categoryQueries
    private val recurringQueries = database.recurringExpenseQueries
    private val financingQueries = database.financingQueries
    private val budgetQueries = database.budgetQueries
    private val savingsGoalQueries = database.savingsGoalQueries
    private val favoriteQueries = database.favoriteTemplateQueries

    private val piggyBankQueries = database.piggyBankQueries

    suspend fun getAllAccounts(): List<AccountEntity> =
        accountQueries.getAllAccounts().awaitAsList()

    suspend fun insertAccount(account: AccountEntity) = withContext(Dispatchers.Default) {
        accountQueries.upsert(
            id = account.id,
            name = account.name.ifBlank { "NUOVO CONTO" },
            logoEmoji = account.logoEmoji,
            logoUri = account.logoUri,
            isVisible = account.isVisible,
            isHidden = account.isHidden,
            isSavings = account.isSavings,
            isArchived = account.isArchived,
            isDeleted = account.isDeleted,
            createdAtEpochMs = account.createdAtEpochMs,
            updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
        )
    }

    suspend fun getAllTransactions(): List<TransactionEntity> =
        transactionQueries.getAllTransactions().awaitAsList()

    suspend fun insertTransaction(tx: TransactionEntity) = withContext(Dispatchers.Default) {
        // 🚀 FIX: Impedisce la sparizione forzando ID minimi necessari per le Foreign Keys
        val safeAccountId = tx.accountId?.takeIf { it.isNotBlank() }
        val safeCategoryId = tx.categoryId.takeIf { it.isNotBlank() } ?: "default"

        transactionQueries.upsert(
            id = tx.id,
            accountId = safeAccountId,
            toAccountId = tx.toAccountId?.takeIf { it.isNotBlank() },
            fromAccountId = tx.fromAccountId?.takeIf { it.isNotBlank() },
            amountCents = tx.amountCents,
            type = tx.type,
            dateEpochDay = tx.dateEpochDay,
            categoryId = safeCategoryId,
            description = tx.description ?: "",
            merchantKey = tx.merchantKey,
            subcategoryText = tx.subcategoryText,
            logoEmoji = tx.logoEmoji,
            logoUri = tx.logoUri,
            isHidden = tx.isHidden,
            isDeleted = tx.isDeleted,
            reminderDays = tx.reminderDays,
            createdAtEpochMs = tx.createdAtEpochMs,
            updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
        )
    }

    suspend fun getAllCategories(): List<CategoryEntity> =
        categoryQueries.getAllCategories().awaitAsList()

    suspend fun insertCategory(cat: CategoryEntity) = withContext(Dispatchers.Default) {
        if (cat.id.isBlank()) return@withContext
        categoryQueries.upsert(
            id = cat.id,
            name = cat.name.ifBlank { "SENZA NOME" },
            isDeleted = cat.isDeleted,
            createdAtEpochMs = cat.createdAtEpochMs,
            updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
        )
    }

    suspend fun getAllRecurring(): List<RecurringExpenseEntity> =
        recurringQueries.all().awaitAsList()

    suspend fun insertRecurring(item: RecurringExpenseEntity) = withContext(Dispatchers.Default) {
        // 🚀 FIX: Protezione critica per le spese ricorrenti
        val safeCatId = item.categoryId.takeIf { it.isNotBlank() } ?: "default"
        val safeAccId = item.accountId.takeIf { it.isNotBlank() }
            ?: accountQueries.getAllAccounts().awaitAsList().firstOrNull()?.id
            ?: "default_account"

        recurringQueries.upsert(
            id = item.id,
            name = item.name.ifBlank { "RICORRENTE" },
            type = item.type, // 🚀 PRO FIX: IL CAMPO AGGIUNTO!
            amountCents = item.amountCents,
            accountId = safeAccId,
            toAccountId = item.toAccountId?.takeIf { it.isNotBlank() },
            categoryId = safeCatId,
            dayOfMonth = item.dayOfMonth,
            isEnabled = item.isEnabled,
            isDeleted = item.isDeleted,
            cycleTarget = item.cycleTarget,
            logoEmoji = item.logoEmoji,
            logoUri = item.logoUri,
            notes = item.notes,
            autoGenerateOnReminder = item.autoGenerateOnReminder,
            nextEpochDay = item.nextEpochDay,
            frequency = item.frequency,
            isPaused = item.isPaused,
            startEpochDay = item.startEpochDay,
            createdAtEpochMs = item.createdAtEpochMs,
            updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
        )
    }

    suspend fun getAllFinancings(): List<FinancingEntity> =
        financingQueries.all().awaitAsList()

    suspend fun getAllBudgets(): List<BudgetEntity> =
        budgetQueries.all().awaitAsList()

    suspend fun getAllSavingsGoals(): List<SavingsGoalEntity> =
        savingsGoalQueries.all().awaitAsList()

    suspend fun getAllFavorites(): List<FavoriteTemplateEntity> =
        favoriteQueries.all().awaitAsList()

    suspend fun deleteTransaction(id: String) = withContext(Dispatchers.Default) {
        transactionQueries.deleteById(id)
    }

    suspend fun deleteAccount(id: String) = withContext(Dispatchers.Default) {
        // Invece di cancellare fisicamente, mettiamo isDeleted = true per non rompere i movimenti esistenti
        accountQueries.deleteById(id)
    }
    suspend fun getAllPiggyBanks(): List<PiggyBankEntity> =
        piggyBankQueries.all().awaitAsList()
}